/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { JobStatus, TaskStatus, TaskType } from '@g17eco/types/background-jobs';
import {
  RowApprovalStatus,
  type DataTransferJob,
  type DataTransferPlain,
  type DataTransferTask,
  type DataTransferMetricTaskData,
  type ValidationNotice,
} from '@g17eco/types/data-transfer';
import type { SubConnection } from '@g17eco/types/app-integration';
import type { UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import type { ValueListPlain } from '@g17eco/types/valueList';

export type ReviewFilter = 'all' | 'actionable' | 'pending' | 'approved' | 'rejected' | 'skipped';

export const ACTIONABLE = new Set([RowApprovalStatus.Pending, RowApprovalStatus.Approved, RowApprovalStatus.Rejected]);

export const matchesFilter = (approval: RowApprovalStatus, filter: ReviewFilter): boolean => {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'actionable') {
    return ACTIONABLE.has(approval);
  }
  return approval === filter;
};

export const formatValue = (value?: string | number | string[], unit?: string): string => {
  if (value === undefined || value === null) {
    return '—';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'number' && unit) {
    return `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} ${unit}`;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return unit ? `${value} ${unit}` : value;
};

const NUMERIC_VALUE_TYPES = new Set(['number', 'percentage', 'sample', 'calculation', 'numericValueList']);

export const isNumericValueType = (valueType?: string): boolean =>
  Boolean(valueType && NUMERIC_VALUE_TYPES.has(valueType));


export const formatUtrCode = (code: string): string => code.replace('gri/2020/', 'GRI ').replace('/', '-');

export const getGriSection = (code: string): string => {
  const match = code.match(/gri\/2020\/(\d+)/);
  if (!match) {
    return '';
  }
  const sectionMap: Record<string, string> = { '301': 'Materials', '302': 'Energy', '303': 'Water & Effluents', '305': 'Emissions', '306': 'Waste' };
  return sectionMap[match[1]] ?? `GRI ${match[1]}`;
};

export const getSubsidiaryLabel = (sc: SubConnection): string => sc.data?.facilityName ?? sc.name;

export const buildSubsidiaryMap = (subConnections: SubConnection[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const sc of subConnections) {
    map.set(sc.initiativeId, getSubsidiaryLabel(sc));
  }
  return map;
};

export const isMetricTask = (task: DataTransferTask): boolean => {
  return task.type === TaskType.DataTransferMetric;
};

export const getMetricTasks = (job: DataTransferJob): DataTransferTask[] => {
  return job.tasks.filter(isMetricTask);
};

/**
 * Resolve the effective approval for a task by merging all reviews.
 * For simple value tasks: latest reviews[].approval wins.
 * For multi-value tasks (entries): walk newest-to-oldest per entry key.
 */
export const resolveTaskApproval = (data: DataTransferMetricTaskData): RowApprovalStatus => {
  if (!data.reviews || data.reviews.length === 0) {
    return data.autoClassification;
  }

  // Task-level approval (newest review wins) — applies to both simple and multi-entry tasks
  for (let i = data.reviews.length - 1; i >= 0; i--) {
    const approval = data.reviews[i].approval;
    if (approval) {
      return approval;
    }
  }

  // For multi-entry tasks, derive approval from entry-level reviews (all-or-nothing)
  if (data.entries) {
    const entryApprovals = resolveEntryApprovals(data);
    if (entryApprovals.size > 0 && entryApprovals.size >= data.entries.length) {
      const values = Array.from(entryApprovals.values());
      if (values.some((a) => a === RowApprovalStatus.Approved)) {
        return RowApprovalStatus.Approved;
      }
      if (values.every((a) => a === RowApprovalStatus.Rejected)) {
        return RowApprovalStatus.Rejected;
      }
    }
  }

  return data.autoClassification;
};

/**
 * Resolve per-entry approvals by merging all reviews newest-to-oldest.
 */
export const resolveEntryApprovals = (data: DataTransferMetricTaskData): Map<string, RowApprovalStatus> => {
  const approvalMap = new Map<string, RowApprovalStatus>();

  if (data.reviews) {
    for (let i = data.reviews.length - 1; i >= 0; i--) {
      const review = data.reviews[i];
      if (review.entries) {
        for (const entry of review.entries) {
          if (!approvalMap.has(entry.key)) {
            approvalMap.set(entry.key, entry.approval);
          }
        }
      }
    }
  }

  return approvalMap;
};

/** A job that hasn't produced metric tasks is considered stuck if it hasn't
 *  been updated in this many milliseconds. Shows a retry button on the dashboard. */
export const STUCK_GENERATE_THRESHOLD_MS = 15 * 60 * 1000;

export interface SubsidiaryGroup {
  subsidiaryId: string;
  jobs: DataTransferJob[];
  /** All jobs have metric tasks generated (ready for review) */
  allGenerated: boolean;
  /** Jobs whose generate task has not produced metric tasks yet */
  ungeneratedJobCount: number;
  /** Jobs waiting to start (Pending or WaitForRun, not yet actively processing) */
  queuedJobCount: number;
  /** Jobs that appear stuck (no metric tasks + not updated in 15 min) */
  stuckJobCount: number;
  /** Jobs whose generate task is re-running (regenerating snapshot) */
  refreshingJobCount: number;
  pendingCount: number;
  skippedCount: number;
  flaggedCount: number;
  approvedCount: number;
  syncedCount: number;
  errorCount: number;
  totalTasks: number;
}

export const getJobSubsidiaryGroups = (dataTransfer: DataTransferPlain, jobs: DataTransferJob[]): SubsidiaryGroup[] => {
  const jobMap = new Map<string, DataTransferJob>();
  for (const job of jobs) {
    jobMap.set(job._id, job);
  }

  const groupMap = new Map<string, SubsidiaryGroup>();

  for (const report of dataTransfer.reports) {
    const job = jobMap.get(report.jobId);
    if (!job) {
      continue;
    }

    let group = groupMap.get(report.initiativeId);
    if (!group) {
      group = {
        subsidiaryId: report.initiativeId,
        jobs: [],
        allGenerated: true,
        ungeneratedJobCount: 0,
        queuedJobCount: 0,
        stuckJobCount: 0,
        refreshingJobCount: 0,
        pendingCount: 0,
        skippedCount: 0,
        flaggedCount: 0,
        approvedCount: 0,
        syncedCount: 0,
        errorCount: 0,
        totalTasks: 0,
      };
      groupMap.set(report.initiativeId, group);
    }

    group.jobs.push(job);

    const metricTasks = getMetricTasks(job);

    // Job is "generated" once it has metric tasks (snapshots taken)
    const generateTask = job.tasks.find((t) => t.type === TaskType.DataTransferGenerate);
    if (metricTasks.length === 0) {
      group.allGenerated = false;
      group.ungeneratedJobCount++;
      if (job.status === JobStatus.Pending || job.status === JobStatus.WaitForRun) {
        group.queuedJobCount++;
      }
      if (job.updated && Date.now() - new Date(job.updated).getTime() > STUCK_GENERATE_THRESHOLD_MS) {
        group.stuckJobCount++;
      }
    } else if (generateTask?.status === TaskStatus.Pending) {
      // Has metric tasks but generate task is re-running (regenerate was triggered)
      if (job.updated && Date.now() - new Date(job.updated).getTime() > STUCK_GENERATE_THRESHOLD_MS) {
        group.stuckJobCount++;
      } else {
        group.refreshingJobCount++;
      }
    }
    group.totalTasks += metricTasks.length;

    for (const task of metricTasks) {
      const approval = resolveTaskApproval(task.data);
      switch (approval) {
        case RowApprovalStatus.Pending:
          group.pendingCount++;
          break;
        case RowApprovalStatus.Skipped:
          group.skippedCount++;
          break;
        case RowApprovalStatus.Flagged:
          group.flaggedCount++;
          break;
        case RowApprovalStatus.Approved:
          if (!task.data.sync) {
            group.approvedCount++;
          }
          break;
      }
      if (task.data.sync?.status === 'synced') {
        group.syncedCount++;
      } else if (task.data.sync?.status === 'error') {
        group.errorCount++;
      }
    }
  }

  return Array.from(groupMap.values());
};

/**
 * A period job is considered stuck when its generate task has been Pending
 * for longer than STUCK_GENERATE_THRESHOLD_MS without the job completing.
 * Used by both the dashboard (stuck badge + retry) and the review page.
 */
export const isRefreshStuck = (job: DataTransferJob, now: number = Date.now()): boolean => {
  if (job.status === JobStatus.Completed) {
    return false;
  }
  const generateTask = job.tasks.find((t) => t.type === TaskType.DataTransferGenerate);
  if (generateTask?.status !== TaskStatus.Pending) {
    return false;
  }
  if (!job.updated) {
    return false;
  }
  return now - new Date(job.updated).getTime() > STUCK_GENERATE_THRESHOLD_MS;
};

export const getGenerateTaskNotices = (job: DataTransferJob): ValidationNotice[] => {
  // Generate task data is typed as metric data, but older generate tasks have no notices
  const data: { notices?: ValidationNotice[] } | undefined = job.tasks.find((t) => t.type === TaskType.DataTransferGenerate)?.data;
  return data?.notices ?? [];
};

export type DiffCategory = 'increase' | 'decrease' | 'none';

export interface DiffPercentage {
  category: DiffCategory;
  /** Signed percentage change from survey to proposed, or undefined when not computable. */
  pct?: number;
}

/**
 * Percentage difference between the current survey value and the proposed
 * integration value. Returns `none` when either value is missing, non-numeric,
 * or the survey value is zero (no sensible relative delta).
 */
export const computeDiffPercentage = (
  survey?: string | number | string[],
  proposed?: string | number | string[],
): DiffPercentage => {
  if (survey === undefined || proposed === undefined || Array.isArray(survey) || Array.isArray(proposed)) {
    return { category: 'none' };
  }
  const s = typeof survey === 'number' ? survey : Number(survey);
  const p = typeof proposed === 'number' ? proposed : Number(proposed);
  if (Number.isNaN(s) || Number.isNaN(p) || s === 0) {
    return { category: 'none' };
  }
  const pct = ((p - s) / Math.abs(s)) * 100;
  if (pct > 0) {
    return { category: 'increase', pct };
  }
  if (pct < 0) {
    return { category: 'decrease', pct };
  }
  return { category: 'none', pct };
};

/**
 * Turn a snake_case entry key into title-case for display
 * (e.g. `waste_type` → `Waste Type`).
 */
export const formatEntryKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Resolve an option/column code to its human-readable label by consulting the
 * UTR's value validation. Falls back to title-casing the code when no label
 * is found. `optionCode` may be undefined (e.g. simple-value rows have no
 * column), in which case an empty string is returned.
 */
export const resolveEntryLabel = (
  utrCode: string,
  optionCode: string | undefined,
  utrByCode: Map<string, UniversalTrackerPlain>,
  valueListById: Map<string, ValueListPlain>,
): string => {
  if (!optionCode) {
    return '';
  }
  const utr = utrByCode.get(utrCode);
  if (!utr) {
    return formatEntryKey(optionCode);
  }
  const vv = utr.valueValidation;
  const column = vv?.table?.columns?.find((c: { code: string }) => c.code === optionCode);
  if (column) {
    return (column as { name: string }).name;
  }
  const listId = (vv?.valueList as { listId?: string } | undefined)?.listId;
  if (listId) {
    const valueList = valueListById.get(listId);
    const option = valueList?.options?.find((o: { code: string }) => o.code === optionCode);
    if (option) {
      return (option as { name: string }).name;
    }
  }
  return formatEntryKey(optionCode);
};

export const resolveGroupStatus = (group: SubsidiaryGroup): string => {
  if (!group.allGenerated) {
    if (group.stuckJobCount > 0 && group.stuckJobCount === group.ungeneratedJobCount) {
      return 'stuck';
    }
    if (group.queuedJobCount === group.ungeneratedJobCount) {
      return 'queued';
    }
    return 'generating';
  }
  if (group.stuckJobCount > 0) {
    return 'stuck';
  }
  if (group.refreshingJobCount > 0) {
    return 'generating';
  }
  if (group.errorCount > 0) {
    return 'error';
  }
  if (group.syncedCount > 0) {
    return 'synced';
  }
  if (group.skippedCount === group.totalTasks) {
    return 'skipped';
  }
  if (group.pendingCount > 0) {
    return 'pending';
  }
  return 'ready';
};

export const getGroupReviewProgress = (group: SubsidiaryGroup): { reviewed: number; actionable: number } => {
  const actionable = group.totalTasks - group.skippedCount - group.flaggedCount;
  const reviewed = actionable - group.pendingCount;
  return { reviewed, actionable };
};

export const latestJobUpdate = (jobs: DataTransferJob[]): string | undefined => {
  if (jobs.length === 0) {
    return undefined;
  }
  return jobs.reduce((latest, j) => (new Date(j.updated).getTime() > new Date(latest).getTime() ? j.updated : latest), jobs[0].updated);
};
